/**
 * TanStack Query hook for fetching run details and quarantine data.
 */

import { useQuery } from "@tanstack/react-query"
import { sdk } from "@/services/sdk"
import type { GetRunResponseContent, QuarantineRecord } from "./types"
import { TERMINAL_STATUSES } from "./types"

/**
 * Key factory for run detail queries.
 */
export const runDetailKeys = {
  all: (studyId: string, runId: string) => ["run-detail", studyId, runId] as const,
  quarantine: (studyId: string, runId: string) =>
    ["run-detail", studyId, runId, "quarantine"] as const,
}

/**
 * Return type for the useRunDetail hook.
 */
interface UseRunDetailResult {
  /**
   * The fetched run data, or undefined while loading.
   */
  run: GetRunResponseContent | undefined
  /**
   * Quarantined records for failed/partially completed runs.
   */
  quarantineRecords: QuarantineRecord[]
  /**
   * Whether the initial run data is still loading.
   */
  isLoading: boolean
  /**
   * Whether quarantine records are still loading.
   */
  isLoadingQuarantine: boolean
  /**
   * Error from the run or quarantine query, if any.
   */
  error: Error | null
  /**
   * True when the run is not in a terminal status (still polling).
   */
  isLive: boolean
}

/**
 * Fetches run details with polling for active runs, and quarantine data for failed runs.
 */
export function useRunDetail(studyId: string, runId: string): UseRunDetailResult {
  const runQuery = useQuery({
    queryKey: runDetailKeys.all(studyId, runId),
    queryFn: () => sdk.getStudyRun({ studyId, runId }),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status && TERMINAL_STATUSES.includes(status)) return false
      return 3000
    },
  })

  const run = runQuery.data
  const isLive = !!run && !TERMINAL_STATUSES.includes(run.status)

  const quarantineQuery = useQuery({
    queryKey: runDetailKeys.quarantine(studyId, runId),
    queryFn: () => sdk.getStudyRunQuarantineData({ studyId, runId, pageSize: 50 }),
    enabled: !!run && ["FAILED", "PARTIALLY_COMPLETED"].includes(run.status),
  })

  const quarantineRecords = (quarantineQuery.data?.records ?? []) as QuarantineRecord[]

  return {
    run,
    quarantineRecords,
    isLoading: runQuery.isLoading,
    isLoadingQuarantine: quarantineQuery.isLoading,
    error: runQuery.error ?? quarantineQuery.error ?? null,
    isLive,
  }
}
